import { cloneElement, useCallback, useEffect, useId, useRef, useState } from 'react';
import Button from './button.jsx';

export function AlertDialog({
  trigger,
  title,
  description,
  confirmLabel = 'Aceptar',
  cancelLabel = 'Cancelar',
  onConfirm,
  variant = 'default',
  pendingLabel = 'Procesando...',
  children,
  open: controlledOpen,
  onOpenChange,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);
  const triggerRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();
  const open = controlledOpen ?? internalOpen;

  const setOpen = useCallback((value) => {
    if (controlledOpen === undefined) setInternalOpen(value);
    onOpenChange?.(value);
  }, [controlledOpen, onOpenChange]);
  const close = useCallback(() => {
    if (submitting) return;
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  }, [setOpen, submitting]);
  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const result = await onConfirm?.();
      if (result !== false) setOpen(false);
    } catch {
      // El consumidor muestra el error y el diálogo permanece abierto.
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    cancelRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...dialogRef.current.querySelectorAll('button:not(:disabled), [href], [tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [close, open]);

  return (
    <>
      {trigger ? cloneElement(trigger, {
        ref: triggerRef,
        'aria-haspopup': 'dialog',
        'aria-expanded': open,
        onClick: (event) => {
          trigger.props.onClick?.(event);
          setOpen(true);
        },
      }) : null}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <div
            ref={dialogRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={description ? descriptionId : undefined}
            className="w-full max-w-md rounded-2xl border border-[rgba(var(--border),0.72)] bg-[rgb(var(--panel))] p-6 text-[rgb(var(--fg))] shadow-[0_18px_48px_rgba(var(--shadow),0.2)]"
          >
            <h3 id={titleId} className="text-lg font-semibold">{title}</h3>
            {description && (
              <p id={descriptionId} className="mt-2 text-sm leading-6 text-[rgba(var(--muted),0.96)]">{description}</p>
            )}
            {children ? <div className="mt-4">{children}</div> : null}
            <div className="mt-6 flex justify-end gap-2">
              <Button ref={cancelRef} variant="ghost" onClick={close} disabled={submitting}>
                {cancelLabel}
              </Button>
              <Button variant={variant} onClick={handleConfirm} disabled={submitting} aria-busy={submitting}>
                {submitting ? pendingLabel : confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AlertDialog;
